import React, {useEffect, useState} from 'react'
import {
  Box,
  Grid,
  Stack,
  Typography,
  Autocomplete,
  TextField,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material'
import {useSnackbar} from 'notistack'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import DownloadIcon from '@mui/icons-material/Download'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import RestartAltIcon from '@mui/icons-material/RestartAlt'

import SearchOffOutlinedIcon from '@mui/icons-material/SearchOffOutlined'

import {
  FormCard,
  FormHeader,
  FormHeaderIconBox,
  FormHeaderTextGroup,
  FormSectionTitle,
  FormSectionSubtitle,
  FormBody,
} from './styled'

import {
  useBulkServiceability,
  ACTION_TYPE_LABELS,
  SHIPMENT_TYPE_B2B,
  STATUS_COLORS,
  pincodeTypesFor,
} from './useBulkServiceability'

const ROWS_PER_PAGE = 20
const EXPORT_MAX_ROWS = 10000

// shipmentType 'B2B' (PTL): only entries that targeted B2B account codes (filtered by the API).
export default function AuditLog({parentCps = [], shipmentType}) {
  const {fetchAuditLog} = useBulkServiceability({shipmentType})
  const isB2b = shipmentType === SHIPMENT_TYPE_B2B
  const {labels: pincodeTypeLabels} = pincodeTypesFor(shipmentType)
  const {enqueueSnackbar} = useSnackbar()

  const [filters, setFilters] = useState({
    parentCp: '',
    userId: '',
    startDate: '',
    endDate: '',
  })
  const [data, setData] = useState({content: [], totalElements: 0, totalPages: 0})
  const [page, setPage] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    const params = {page, size: ROWS_PER_PAGE}
    if (filters.parentCp) params.parentCp = filters.parentCp
    if (filters.userId) params.userId = filters.userId
    if (filters.startDate) params.startDate = filters.startDate
    if (filters.endDate) params.endDate = filters.endDate

    fetchAuditLog(params).then((res) => {
      if (cancelled) return
      setData(res || {content: [], totalElements: 0, totalPages: 0})
      setIsLoading(false)
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page])

  const updateFilter = (k, v) => {
    setPage(0)
    setFilters((p) => ({...p, [k]: v}))
  }

  const resetFilters = () => {
    setPage(0)
    setFilters({parentCp: '', userId: '', startDate: '', endDate: ''})
  }

  const hasFilters =
    filters.parentCp || filters.userId || filters.startDate || filters.endDate

  /**
   * Export the audit log to CSV. One row per audit entry — multiple uploads for
   * the same parent on the same day come out as separate rows (which is what
   * users need to slice in Excel). We pull a large single page from the backend
   * rather than paginating client-side so the filter set the user already
   * applied carries through to the download verbatim.
   */
  const exportCsv = async () => {
    setIsExporting(true)
    try {
      const params = {page: 0, size: EXPORT_MAX_ROWS}
      if (filters.parentCp) params.parentCp = filters.parentCp
      if (filters.userId) params.userId = filters.userId
      if (filters.startDate) params.startDate = filters.startDate
      if (filters.endDate) params.endDate = filters.endDate

      const res = await fetchAuditLog(params)
      const rows = res?.content || []
      if (rows.length === 0) {
        enqueueSnackbar('Nothing to export for the current filters', {variant: 'warning'})
        return
      }
      if ((res?.totalElements || 0) > EXPORT_MAX_ROWS) {
        enqueueSnackbar(
          `Exporting first ${EXPORT_MAX_ROWS.toLocaleString()} of ${(res.totalElements).toLocaleString()} rows — narrow filters to capture the rest.`,
          {variant: 'warning'}
        )
      }

      const headers = [
        'When',
        'User Name',
        'User ID',
        'Action',
        'Parent CP',
        'Account Code',
        'Pincode Type',
        'Pincode Count',
        'Status',
        'Successful',
        'Failed',
        'File Name',
        'Original CSV URL',
        'Failure Report URL',
      ]

      const escape = (v) => {
        if (v == null) return ''
        const s = String(v)
        // Wrap in quotes if contains comma / quote / newline; double up internal quotes.
        return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
      }

      const lines = [headers.map(escape).join(',')]
      let exportedRows = 0
      rows.forEach((r) => {
        // Fan out: one CSV row per account code. A Bulk Upload that targeted
        // 7 codes produces 7 rows with identical metadata so the file is
        // pivot-friendly in Excel. We still emit a single row when there are
        // no account codes (L1 toggle / deactivate against unknown), so audit
        // entries are never silently dropped.
        const codes = r.accountCodesTargeted && r.accountCodesTargeted.length > 0
          ? r.accountCodesTargeted
          : ['']
        codes.forEach((code) => {
          lines.push(
            [
              r.createdAt ? new Date(r.createdAt).toISOString() : '',
              r.userName || '',
              r.userId || '',
              ACTION_TYPE_LABELS[r.actionType] || r.actionType || '',
              r.parentCp || '',
              code || '',
              r.pincodeType ? pincodeTypeLabels[r.pincodeType] || r.pincodeType : '',
              r.pincodeCount ?? '',
              r.status || '',
              r.successfulCount ?? '',
              r.failedCount ?? '',
              r.fileName || '',
              r.s3OriginalUrl || '',
              r.s3FailureUrl || '',
            ]
              .map(escape)
              .join(',')
          )
          exportedRows += 1
        })
      })

      const csv = '﻿' + lines.join('\n') // BOM so Excel opens UTF-8 cleanly
      const blob = new Blob([csv], {type: 'text/csv;charset=utf-8'})
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const stamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-')
      link.download = `bulk-serv-audit-log-${stamp}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      // Fan-out can make the CSV bigger than the underlying audit count (e.g.
      // a single Bulk Upload across 7 account codes becomes 7 rows). Surface
      // both numbers so the user knows the file isn't double-counted.
      enqueueSnackbar(
        rows.length === exportedRows
          ? `Exported ${exportedRows.toLocaleString()} row${exportedRows === 1 ? '' : 's'}`
          : `Exported ${exportedRows.toLocaleString()} rows from ${rows.length.toLocaleString()} audit entries (one row per account code)`,
        {variant: 'success'}
      )
    } catch (err) {
      enqueueSnackbar('Export failed', {variant: 'error'})
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <FormCard>
      <FormHeader>
        <FormHeaderIconBox>
          <HistoryOutlinedIcon fontSize='small' />
        </FormHeaderIconBox>
        <FormHeaderTextGroup>
          <FormSectionTitle>Audit Log</FormSectionTitle>
          <FormSectionSubtitle>
            {isB2b
              ? 'Full audit trail of every PTL pincode upload and deactivation across PTL account codes.'
              : 'Full audit trail of every upload, L1 toggle, and deactivation across all account codes.'}
          </FormSectionSubtitle>
        </FormHeaderTextGroup>
      </FormHeader>
      <FormBody>
        <Grid container spacing={2} sx={{mb: 2}}>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              size='small'
              options={parentCps}
              value={filters.parentCp || null}
              onChange={(_, v) => updateFilter('parentCp', v || '')}
              renderInput={(params) => <TextField {...params} label='Parent Courier' />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              size='small'
              fullWidth
              label='User ID / Email'
              value={filters.userId}
              onChange={(e) => updateFilter('userId', e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              size='small'
              fullWidth
              type='date'
              label='Start date'
              InputLabelProps={{shrink: true}}
              value={filters.startDate}
              onChange={(e) => updateFilter('startDate', e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              size='small'
              fullWidth
              type='date'
              label='End date'
              InputLabelProps={{shrink: true}}
              value={filters.endDate}
              onChange={(e) => updateFilter('endDate', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Stack direction='row' spacing={1} sx={{height: '100%'}} alignItems='center' justifyContent='flex-end'>
              {hasFilters && (
                <Button
                  size='small'
                  startIcon={<RestartAltIcon fontSize='small' />}
                  onClick={resetFilters}
                  sx={{textTransform: 'none', fontWeight: 600}}
                >
                  Clear
                </Button>
              )}
              <Tooltip
                title={
                  (data.totalElements || 0) === 0
                    ? 'Nothing to export'
                    : `Export ${Math.min(data.totalElements, EXPORT_MAX_ROWS).toLocaleString()} row(s) as CSV`
                }
              >
                <span>
                  <Button
                    size='small'
                    variant='outlined'
                    onClick={exportCsv}
                    disabled={isExporting || (data.totalElements || 0) === 0}
                    startIcon={
                      isExporting ? (
                        <CircularProgress size={14} />
                      ) : (
                        <FileDownloadOutlinedIcon fontSize='small' />
                      )
                    }
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 2,
                      borderColor: 'grey.300',
                      color: 'grey.800',
                      whiteSpace: 'nowrap',
                      '&:hover': {borderColor: 'grey.400', backgroundColor: '#fafafa'},
                    }}
                  >
                    {isExporting ? 'Exporting…' : 'Download CSV'}
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>

        {isLoading ? (
          <Stack alignItems='center' sx={{py: 6}}>
            <CircularProgress size={24} />
          </Stack>
        ) : data.content.length === 0 ? (
          <Stack alignItems='center' sx={{py: 6, px: 3, textAlign: 'center'}} spacing={1}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SearchOffOutlinedIcon sx={{fontSize: 28, color: 'grey.500'}} />
            </Box>
            <Typography sx={{fontSize: '0.92rem', fontWeight: 700, color: 'grey.800'}}>
              {hasFilters ? 'No entries match these filters' : 'No audit entries yet'}
            </Typography>
            <Typography sx={{fontSize: '0.78rem', color: 'grey.600', maxWidth: 360, lineHeight: 1.5}}>
              {hasFilters
                ? 'Try widening the date range or clearing some filters.'
                : isB2b
                ? 'PTL pincode uploads and deactivations will show up here.'
                : 'Uploads, L1 toggles, and deactivations across account codes will show up here.'}
            </Typography>
          </Stack>
        ) : (
          <>
            <TableContainer
              sx={{
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 1.5,
                maxHeight: '60vh',
              }}
            >
              <Table size='small' stickyHeader>
                <TableHead>
                  <TableRow>
                    {[
                      'When',
                      'User',
                      'Action',
                      'Parent CP',
                      'Account Codes',
                      'Pincode Type',
                      'Count',
                      'Status',
                      'Files',
                    ].map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.03em',
                          color: 'grey.700',
                          backgroundColor: '#fafafa',
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.content.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{fontSize: '0.72rem'}}>
                        {row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}
                      </TableCell>
                      <TableCell sx={{fontSize: '0.72rem'}}>
                        <Typography sx={{fontSize: '0.78rem', fontWeight: 600}}>
                          {row.userName || '—'}
                        </Typography>
                        <Typography sx={{fontSize: '0.68rem', color: 'grey.600'}}>
                          {row.userId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size='small'
                          label={ACTION_TYPE_LABELS[row.actionType] || row.actionType}
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            ...(row.actionType === 'BULK_UPLOAD' && {backgroundColor: '#e3f2fd', color: '#1565c0'}),
                            ...(row.actionType === 'PD_BULK_UPLOAD' && {backgroundColor: '#f3e5f5', color: '#6a1b9a'}),
                            ...(row.actionType === 'L1_TOGGLE' && {backgroundColor: '#e8f5e9', color: '#2e7d32'}),
                            ...(row.actionType === 'DEACTIVATE' && {backgroundColor: '#ffebee', color: '#c62828'}),
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{fontSize: '0.72rem'}}>{row.parentCp || '—'}</TableCell>
                      <TableCell sx={{fontSize: '0.72rem', fontFamily: 'monospace', maxWidth: 220}}>
                        {row.accountCodesTargeted?.length > 1 ? (
                          <Tooltip title={row.accountCodesTargeted.join(', ')}>
                            <span>
                              {row.accountCodesTargeted[0]}{' '}
                              <Chip
                                size='small'
                                label={`+${row.accountCodesTargeted.length - 1}`}
                                sx={{height: 16, fontSize: '0.6rem', ml: 0.5}}
                              />
                            </span>
                          </Tooltip>
                        ) : (
                          row.accountCodesTargeted?.[0] || '—'
                        )}
                      </TableCell>
                      <TableCell sx={{fontSize: '0.72rem'}}>
                        {row.pincodeType ? (pincodeTypeLabels[row.pincodeType] || row.pincodeType) : '—'}
                      </TableCell>
                      <TableCell sx={{fontSize: '0.72rem', fontWeight: 600}}>
                        {row.pincodeCount?.toLocaleString() || '—'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size='small'
                          label={
                            row.status === 'COMPLETED'
                              ? `${row.successfulCount || 0}/${(row.successfulCount || 0) + (row.failedCount || 0)}`
                              : row.status
                          }
                          color={STATUS_COLORS[row.status] || 'default'}
                          variant='outlined'
                          sx={{height: 20, fontSize: '0.65rem'}}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction='row' spacing={0.25}>
                          {row.s3OriginalUrl && (
                            <Tooltip title={row.fileName || 'Original CSV'}>
                              <IconButton
                                size='small'
                                component='a'
                                href={row.s3OriginalUrl}
                                target='_blank'
                                rel='noreferrer'
                              >
                                <OpenInNewIcon sx={{fontSize: 14}} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {row.s3FailureUrl && (
                            <Tooltip title='Failure report'>
                              <IconButton
                                size='small'
                                component='a'
                                href={row.s3FailureUrl}
                                target='_blank'
                                rel='noreferrer'
                                sx={{color: 'warning.main'}}
                              >
                                <DownloadIcon sx={{fontSize: 14}} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component='div'
              count={data.totalElements || 0}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={ROWS_PER_PAGE}
              rowsPerPageOptions={[ROWS_PER_PAGE]}
              sx={{
                '& .MuiTablePagination-toolbar': {minHeight: 44},
                '& .MuiTablePagination-displayedRows': {fontSize: '0.72rem'},
              }}
            />
          </>
        )}
      </FormBody>
    </FormCard>
  )
}
